import * as React from 'react';

import {
  Badge,
  Button,
  ConfirmPopoverButton,
  DialogClosingConfirmation,
  EntityDetails,
  LocalizedDate,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ArrowLeft, Trash2 } from 'lucide-react';

import { itemAliasDeleteAtom } from '../../../_graphql/mutationItemAliasDelete';
import { itemAliasUpdateAtom } from '../../../_graphql/mutationItemAliasUpdate';
import * as state from '../../../_state/itemAliasDetail.state';
import {
  $hasItemAliasUnsavedChanges,
  $itemAliasDiscardBaseline,
  $resetItemAliasFormToBaseline,
  ItemAliasFormState,
} from '../../../_state/itemAliasForm.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { usePopulateItemAliasForm } from '../../../hooks/usePopulateItemAliasForm';
import { useTranslations } from '../../../i18n/useTranslations';

import { ItemAliasTabDetails } from './ItemAliasTabDetails';

type ItemAliasDetailTab = 'details';

export const ItemAliasDetailView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();

  const [currentTab, setCurrentTab] =
    React.useState<ItemAliasDetailTab>('details');
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [pendingTabChange, setPendingTabChange] =
    React.useState<ItemAliasDetailTab | null>(null);

  const itemAliasId = useAtomValue(state.$itemAliasId);
  const { data, isFetching, refetch } = useAtomValue(
    state.$remoteItemAliasDetail,
  );
  const itemAlias = data?.itemAlias;
  const isLoading = useAtomValue(state.$isItemAliasLoading);
  const error = useAtomValue(state.$itemAliasError);
  const hasUnsavedChanges = useAtomValue($hasItemAliasUnsavedChanges);

  const [, resetForm] = useAtom($resetItemAliasFormToBaseline);
  const [discardBaseline, setDiscardBaseline] = useAtom(
    $itemAliasDiscardBaseline,
  );

  const deleteItemAlias = useSetAtom(itemAliasDeleteAtom({}));
  const updateItemAlias = useSetAtom(itemAliasUpdateAtom({}));

  const toast = useToast();
  usePopulateItemAliasForm({ itemAlias });

  const handleBack = React.useCallback(() => {
    setLocation('/inventory');
  }, [setLocation]);

  const handleDiscard = React.useCallback(() => {
    resetForm();
    toast.success(t.inventoryView.itemAliasDetailView.toast.discarded);
  }, [resetForm, toast, t]);

  const handleSaveUpdates = React.useCallback(
    async (data?: ItemAliasFormState) => {
      if (!itemAliasId || !data) return;

      if (!data.aliasName.trim()) {
        toast.error(
          t.inventoryView.itemAliasCreateDialog.validation.aliasNameRequired,
        );
        return;
      }

      try {
        setIsUpdating(true);
        await updateItemAlias({
          id: itemAliasId,
          aliasName: data.aliasName.trim(),
          aliasCode: data.aliasCode.trim() || null,
          aliasType: data.aliasType,
          notes: data.notes.trim() || null,
        });

        setDiscardBaseline(data);
        toast.success(t.inventoryView.itemAliasDetailView.toast.updated);
        refetch?.();
      } catch (err) {
        toast.error(
          t.inventoryView.itemAliasDetailView.toast.updateError(
            err instanceof Error ? err.message : 'Unknown error',
          ),
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [itemAliasId, updateItemAlias, setDiscardBaseline, toast, t, refetch],
  );

  const handleBeforeChangeTab = React.useCallback(
    (newTab: string) => {
      if (hasUnsavedChanges) {
        setPendingTabChange(newTab as ItemAliasDetailTab);
        return false;
      }
      return true;
    },
    [hasUnsavedChanges],
  );

  const handleConfirmTabChange = React.useCallback(() => {
    if (pendingTabChange && discardBaseline) {
      resetForm();
      setCurrentTab(pendingTabChange);
    }
    setPendingTabChange(null);
  }, [pendingTabChange, discardBaseline, resetForm]);

  const handleCancelTabChange = React.useCallback(() => {
    setPendingTabChange(null);
  }, []);

  const handleDelete = React.useCallback(async () => {
    if (!itemAliasId) return;

    try {
      setIsDeleting(true);
      await deleteItemAlias({ id: itemAliasId });
      toast.success(t.inventoryView.itemAliasDetailView.messages.deleteSuccess);
      setLocation('/inventory');
    } catch (err) {
      toast.error(
        t.inventoryView.itemAliasDetailView.messages.deleteError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsDeleting(false);
    }
  }, [itemAliasId, deleteItemAlias, toast, setLocation, t]);

  const tools = React.useMemo(() => {
    if (!itemAlias) return null;
    return (
      <ConfirmPopoverButton
        onConfirm={handleDelete}
        message={
          t.inventoryView.itemAliasDetailView.messages.deleteConfirmation
        }
        confirmLabel={t.inventoryView.common.confirmDelete}
        cancelLabel={t.inventoryView.common.cancel}
        buttonProps={{
          variant: 'outline',
          color: 'failure',
          icon: <Trash2 className="size-4" />,
          isDisabled: isDeleting,
        }}
      >
        {isDeleting
          ? t.inventoryView.itemAliasDetailView.actions.deleting
          : t.inventoryView.itemAliasDetailView.actions.delete}
      </ConfirmPopoverButton>
    );
  }, [itemAlias, handleDelete, isDeleting, t]);

  if (isLoading || isFetching) {
    return (
      <div className="@ItemAliasDetailView flex h-full flex-col">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-1/4 rounded bg-neutral/20"></div>
            <div className="h-8 w-1/3 rounded bg-neutral/20"></div>
            <div className="h-32 rounded bg-neutral/20"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !itemAlias) {
    return (
      <div className="@ItemAliasDetailView flex h-full flex-col p-6">
        <div className="text-failure">
          Error:{' '}
          {error?.message ??
            t.inventoryView.itemAliasDetailView.messages.itemAliasNotFound}
        </div>
        <Button variant="outline" onPress={handleBack} className="mt-4 w-fit">
          <ArrowLeft className="mr-2 size-4" />
          {t.inventoryView.itemAliasDetailView.actions.backToItemAliases}
        </Button>
      </div>
    );
  }

  return (
    <div className="@ItemAliasDetailView flex h-full flex-col">
      <EntityDetails
        className="relative"
        onChangeTab={(tab) => setCurrentTab(tab as ItemAliasDetailTab)}
        onBeforeChangeTab={handleBeforeChangeTab}
        currentTab={currentTab}
        onBackPressed={handleBack}
        primaryTitle={itemAlias.aliasName}
        subtitle={
          <>
            {itemAlias.aliasCode && (
              <>
                <span className="font-mono">{itemAlias.aliasCode}</span>
                <span className="mx-2">•</span>
              </>
            )}
            {t.inventoryView.itemAliasDetailView.fields.created}{' '}
            <LocalizedDate date={itemAlias.timestamps.createdAt} />
          </>
        }
        badges={
          <div className="flex gap-2">
            <Badge
              color={itemAlias.aliasType === 'Buy' ? 'primary' : 'success'}
              size="small"
            >
              {itemAlias.aliasType === 'Buy'
                ? t.inventoryView.itemAliasCreateDialog.aliasTypes.buy
                : t.inventoryView.itemAliasCreateDialog.aliasTypes.sell}
            </Badge>
            <Badge color="neutral" size="small">
              {itemAlias.item.name}
            </Badge>
            <Badge color="neutral" size="small">
              {itemAlias.account.name}
            </Badge>
          </div>
        }
        headerActions={tools}
        tabs={{
          details: {
            title: t.inventoryView.itemAliasDetailView.tabs.details,
            content: () => (
              <ItemAliasTabDetails
                itemAlias={itemAlias}
                onDiscard={handleDiscard}
                onSaveUpdates={handleSaveUpdates}
                isSaving={isUpdating}
              />
            ),
          },
        }}
      />
      <DialogClosingConfirmation
        isOpen={!!pendingTabChange}
        message={
          t.inventoryView.itemAliasDetailView.messages.unsavedDataMessage
        }
        onCancel={handleCancelTabChange}
        onDiscard={handleConfirmTabChange}
      />
    </div>
  );
};
